import React, { useEffect, useCallback, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchExternalCallPointReport,
    patchBooking,
    resetExternalCallPointReport,
    resetPatchBookingStatus,
    selectExternalCallPointReportData,
    selectExternalCallPointReportStatus,
    selectExternalCallPointReportError,
    selectPatchBookingStatus,
} from 'redux/slices/sla-reports/external-call-point-report';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import ReportHeader from './report-header';
import TerminalReceptionSummary from './terminal-reception-summary';
import TerminalReceptionRawDataTable from './terminal-reception-raw-data-table';

const excludedHeaderStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const excludedCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const excludedTableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

const excludedSectionContainerStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    backgroundColor: '#fff',
};

const excludedSubTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

const formatExcludedTime = (isoString) => {
    if (!isoString) return '-';
    return moment(isoString).format('HH:mm');
};

const ExcludedDaySection = ({ date, rows }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!rows?.length) {
        return (
            <div>
                <p style={excludedSubTitleStyle}>Excluded {date ? `(${date})` : ''}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No excluded passengers.</p>
            </div>
        );
    }

    return (
        <div style={excludedSectionContainerStyle}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...excludedSubTitleStyle, marginTop: 0, marginBottom: 0 }}>
                    Excluded by location filter ({rows.length}) {date ? `(${date})` : ''}
                </p>
            </div>

            {isOpen && (
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '10px' }}>
                    <table style={excludedTableStyle}>
                        <colgroup>
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '32%' }} />
                            <col style={{ width: '14%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={excludedHeaderStyle}>Pax Name</th>
                                <th style={excludedHeaderStyle}>Passenger ID</th>
                                <th style={excludedHeaderStyle}>Flight</th>
                                <th style={excludedHeaderStyle}>Departure Time</th>
                                <th style={excludedHeaderStyle}>Call Point</th>
                                <th style={excludedHeaderStyle}>Classification</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => {
                                const callPointDisplay = row.callPoint
                                    ? row.callPoint
                                    : (row.hasWaitingTime ? '(unknown)' : '(no location)');
                                const callPointStyle = row.callPoint
                                    ? excludedCellStyle
                                    : { ...excludedCellStyle, color: '#9ca3af', fontStyle: 'italic' };
                                return (
                                    <tr key={row.bookingId || `${row.passengerName}-${idx}`}>
                                        <td style={excludedCellStyle}>{row.passengerName ?? '-'}</td>
                                        <td style={{ ...excludedCellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                        <td style={excludedCellStyle}>{row.flightNumber ?? '-'}</td>
                                        <td style={excludedCellStyle}>{formatExcludedTime(row.departureTime)}</td>
                                        <td style={callPointStyle}>{callPointDisplay}</td>
                                        <td style={excludedCellStyle}>
                                            {row.classification === 'preAdvised' ? 'Pre-Advised' : 'Non-Advised'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const ExternalCallPointReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const [overrideRow, setOverrideRow] = useState(null);
    const dispatch = useDispatch();
    const data = useSelector(selectExternalCallPointReportData);
    const status = useSelector(selectExternalCallPointReportStatus);
    const error = useSelector(selectExternalCallPointReportError);
    const patchStatus = useSelector(selectPatchBookingStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchExternalCallPointReport({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        return () => {
            dispatch(resetExternalCallPointReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
            setOverrideRow(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchExternalCallPointReport({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;
    const [includeNoData, setIncludeNoData] = useState(true);

    return (
        <div>
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
                checkboxes={[
                    {
                        label: 'Include no-data rows (passengers who couldn\'t be qualified)',
                        checked: includeNoData,
                        onChange: (e) => setIncludeNoData(e.target.checked),
                    },
                ]}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading External Call Point report: {error}</p>
            )}

            {data && (
                <>
                    <TerminalReceptionSummary
                        dailyReports={dailyReports}
                        overallSummary={isMultiDay ? data.summary : null}
                    />
                    {!hideBreakdown && (
                        <>
                            <TerminalReceptionRawDataTable
                                dailyReports={dailyReports}
                                timeColumnKey="departureTime"
                                timeColumnLabel="Departure Time"
                                locationColumnKey="callPoint"
                                locationColumnLabel="Call Point"
                                includeNoData={includeNoData}
                                overrideRow={overrideRow}
                                onOverrideRowChange={setOverrideRow}
                                onPatchBooking={(row, pass, reason, kpiKey) => {
                                    if (row?.bookingId) {
                                        dispatch(patchBooking({
                                            bookingId: row.bookingId,
                                            patch: {
                                                externalCallPointReportOverride: {
                                                    [kpiKey]: { pass, passReason: reason },
                                                },
                                            },
                                        }));
                                    }
                                }}
                                isPatching={isPatching}
                            />
                            <div style={{ marginTop: '32px' }}>
                                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '12px' }}>
                                    Excluded Passengers (audit)
                                </h3>
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: 0, marginBottom: '12px' }}>
                                    Departing passengers dropped by the External Call Point location filter.
                                    Use this to spot bookings that should have matched but didn&apos;t.
                                </p>
                                {dailyReports.map((report) => (
                                    <ExcludedDaySection
                                        key={report.date}
                                        date={report.date}
                                        rows={report.excludedRawData || []}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ExternalCallPointReport;
